import React, { useState, useMemo, useCallback } from "react";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useGetChannels } from "api/channels/service";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";
import AddVehicle from "views/components/Deals/AddVehicle";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { getChannelTypeOptions } from "constants/channelTypes";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { useCreateDeal } from "api/deals/service";
import classNames from "classnames";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";

const WholesaleDeals = ({ activeColor = "blue", account, shellThemeVariant = "1" }) => {
  const isTheme2 = shellThemeVariant === "2";
  const { t } = useTranslation();
  const { data: channelsData, isLoading } = useGetChannels("dealer_wholesaler_auction");
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const createDealMutation = useCreateDeal();

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const typeOptions = useMemo(() => getChannelTypeOptions(t), [t]);
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".wholesale-deals-page.wholesale-deals-page--theme2",
            fieldsSelector: ".wholesale-deals-fields",
          })
        : "",
    [isTheme2]
  );

  const handleSelectChannel = useCallback((channelId) => {
    setSelectedChannelId(channelId);
    setShowAddVehicle(true);
  }, []);

  const handleVehicleAdded = (vehicles) => {
    setShowAddVehicle(false);

    const dealDataForAPI = {
      wholesaler: selectedChannelId,
      vehicle: vehicles[0]?.id,
      deal_type: "wholesale_deal",
    };
    createDealMutation.mutate(dealDataForAPI);
  };

  const WholesaleTable = isTheme2 ? ReactTable2 : ReactTable;

  const wholesaleRows = useMemo(
    () =>
      (channelsData?.results || []).map((channel) => ({
        id: channel.documentId,
        channelId: channel.id,
        displayNum: channel.id,
        name: channel.name,
        description: channel.description,
        type:
          typeOptions.find((opt) => opt.value === channel.type)?.label ||
          channel.type,
        status: channel.channel_status
          ? channel.channel_status.charAt(0).toUpperCase() + channel.channel_status.slice(1)
          : "",
        channel,
      })),
    [channelsData?.results, typeOptions]
  );

  const wholesaleColumns = useMemo(
    () => [
      { Header: "#", accessor: "displayNum" },
      { Header: t("wholesaleDeals.name"), accessor: "name" },
      { Header: t("wholesaleDeals.description"), accessor: "description" },
      { Header: t("wholesaleDeals.type"), accessor: "type" },
      { Header: t("wholesaleDeals.status"), accessor: "status" },
      {
        id: "actions",
        Header: "",
        accessor: "channelId",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="text-right">
            <Button
              style={{ ...activeColorStyle, color: "white" }}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectChannel(row.original.channelId);
              }}
            >
              <span>{t("wholesaleDeals.select")}</span>
            </Button>
          </div>
        ),
      },
    ],
    [t, activeColorStyle, handleSelectChannel]
  );

  return (
    <div
      className={classNames("content wholesale-deals-page", {
        "wholesale-deals-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-wholesale-deals-inset": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <style>
          {`
          .wholesale-deals-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .wholesale-deals-card-header h2,
          .wholesale-deals-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .wholesale-deals-page .align-items-center {
            margin-top: 3px;
          }
          .wholesale-deals-page .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
      </style>
      {showAddDeal && <div className={classNames({ "wholesale-deals-fields": isTheme2 })}>
        <ChannelsEmbedded
        activeColor={activeColor}
        isTheme2={isTheme2}
        defaultType="dealer_wholesaler_auction"
        onClose={() => setShowAddDeal(false)}
        onCreated={(created) => {
          if (created?.id) {
          }
        }}
      /></div>}
      {showAddVehicle &&
        <Card>
          <CardBody className={classNames({ "wholesale-deals-fields": isTheme2 })}>
            <AddVehicle
              activeColor={activeColor}
              isTheme2={isTheme2}
              onClose={() => setShowAddVehicle(false)}
              onVehicleAdded={handleVehicleAdded}
            />
          </CardBody>
        </Card>
      }
      {!showAddDeal && !showAddVehicle &&
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="wholesale-deals-card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="mb-0">{t('wholesaleDeals.title')}</h2>
                  <Button
                    size="sm"
                    style={{ ...activeColorStyle, color: 'white' }}
                    onClick={() => setShowAddDeal(true)}
                  >
                    {t('wholesaleDeals.addWholesale')}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div
                  className="inventory-table-wrap"
                  style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
                >
                  <WholesaleTable
                      activeColor={activeColor}
                      data={wholesaleRows}
                      columns={wholesaleColumns}
                      className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
                      manualPagination={false}
                      pageSize={20}
                      loading={isLoading}
                      loadingText={t("wholesaleDeals.loading")}
                      noDataText={t("wholesaleDeals.noDealsFound")}
                      onRowClick={(row) => {
                        if (row?.channelId != null) handleSelectChannel(row.channelId);
                      }}
                    />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      }
    </div>
  );
};

export default WholesaleDeals; 